import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { api, formatApiError, COMPANY_KEY, linkParam } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Loader, ErrorState } from "../../components/common/States";
import { ROLE } from "../../lib/labels";
import { AuthCard } from "./Login";

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    const token = linkParam(params);
    if (!token) { setState({ error: "Lien incomplet" }); return; }
    const isUser = linkParam(params, "type") === "compte";
    api.post(isUser ? "/auth/verify-email" : "/auth/verify-company-email", { token }).then(({ data }) => setState({ data: { ...data, isUser } })).catch((e) => setState({ error: formatApiError(e) }));
  }, [params]);
  return (
    <AuthCard title="Confirmation de l'email">
      {state.loading && <Loader />}
      {state.error && <ErrorState message={state.error} />}
      {state.data?.isUser && (
        <div className="space-y-4 text-sm" data-testid="verify-success">
          <p className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />Votre adresse <b>{state.data.email}</b> est confirmée.</p>
          <Button asChild className="w-full rounded-full" data-testid="verify-go-space"><Link to="/connexion">Accéder à mon espace</Link></Button>
        </div>
      )}
      {state.data && !state.data.isUser && (
        <div className="space-y-4 text-sm" data-testid="verify-success">
          <p className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />L'adresse de <b>{state.data.company_name}</b> est confirmée.</p>
          <p className="text-slate-600">{state.data.status === "pending" ? "Votre demande est transmise à l'équipe VOXA AFRIKA pour validation." : "Vous pouvez soumettre votre demande depuis votre espace."}</p>
          <Button asChild className="w-full rounded-full" data-testid="verify-go-space"><Link to="/app/parametres">Accéder à mon espace</Link></Button>
        </div>
      )}
    </AuthCard>
  );
}

export function Invitation() {
  const [params] = useSearchParams();
  const token = linkParam(params);
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [inv, setInv] = useState({ loading: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.post("/invitations/lookup", { token }).then(({ data }) => setInv({ data })).catch((e) => setInv({ error: formatApiError(e) }));
  }, [token]);
  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/invitations/accept", { token });
      localStorage.setItem(COMPANY_KEY, data.company_id);
      await refresh(); toast.success("Invitation acceptée"); navigate("/app");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const next = encodeURIComponent(`/invitation#token=${token}`);
  return (
    <AuthCard title="Invitation">
      {inv.loading && <Loader />}
      {inv.error && <ErrorState message={inv.error} />}
      {inv.data && (
        <div className="space-y-4 text-sm" data-testid="invitation-card">
          <p><b>{inv.data.invited_by}</b> vous invite à rejoindre <b>{inv.data.company_name}</b> en tant que <b>{ROLE[inv.data.role]}</b>.</p>
          <p className="text-slate-500">Invitation destinée à {inv.data.email}.</p>
          {user ? (
            <Button onClick={accept} disabled={busy} className="w-full rounded-full" data-testid="invitation-accept-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Accepter et rejoindre l'espace</Button>
          ) : (
            <div className="flex flex-col gap-2">
              <Button asChild className="rounded-full" data-testid="invitation-login"><Link to={`/connexion?next=${next}`}>Se connecter</Link></Button>
              <Button asChild variant="outline" className="rounded-full" data-testid="invitation-register"><Link to={`/inscription?next=${next}`}>Créer mon compte</Link></Button>
            </div>
          )}
        </div>
      )}
    </AuthCard>
  );
}
